import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { useToast } from '../components/ToastContainer';
import AgentSettings from '../components/AgentSettings';
import SvarraAdviceBot from '../components/SvarraAdviceBot';
import FeedbackWidget from '../components/FeedbackWidget';
import QRCodeModal from '../components/QRCodeModal';
import MetricsCards from '../components/MetricsCards';
import { useAgents } from '../hooks/useDashboard';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [qrCodeAgent, setQrCodeAgent] = useState<{id: string, businessName: string} | null>(null);

  // React Query hooks
  const { data: agentsData, isLoading, error } = useAgents();
  const agents = agentsData?.agents || [];
  const agentCount = agentsData?.agentCount || { current: 0, max: 5, remaining: 5, canCreateMore: true };

  // Handle React Query error
  React.useEffect(() => {
    if (error) {
      showError('Failed to load your agents');
    }
  }, [error, showError]);

  const handleOpenAgentSettings = (agentId: string) => {
    setSelectedAgentId(agentId);
    setIsSettingsOpen(true);
  };

  const handleCloseAgentSettings = () => {
    setIsSettingsOpen(false);
    setSelectedAgentId('');
  };

  const handleAgentUpdated = () => {
    // React Query will automatically invalidate and refetch when AgentSettings saves
    // No manual refresh needed - handled by the useUpdateAgent mutation
  };

  const handleCopyChatLink = async (agentId: string) => {
    const chatUrl = `${window.location.protocol}//${window.location.host}/chat/${agentId}`;
    
    try {
      await navigator.clipboard.writeText(chatUrl);
      showSuccess('Chat link copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      showError('Failed to copy link to clipboard');
    }
  };

  const handleShowQRCode = (agentId: string, businessName: string) => {
    setQrCodeAgent({ id: agentId, businessName });
  };

  const handleCloseQRCode = () => {
    setQrCodeAgent(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white break-words">
            Welcome back, <span className="bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent">{user?.firstName}</span>!
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-300">
            Manage your AI agents and view analytics from your dashboard.
          </p>
        </div>

        {/* Trial Banner */}
        {user?.subscriptionStatus === 'trialing' && (
          <div className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-green-800 dark:text-green-200">
                    Free Trial Active!
                  </h3>
                  <p className="text-green-700 dark:text-green-300">
                    You have <span className="font-semibold">{user.trialDaysRemaining || 0} days</span> remaining in your free trial. 
                    Enjoy full access to all Svarra features!
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0">
                <Link
                  to="/settings?tab=billing"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-green-800 dark:text-green-200 bg-green-100 dark:bg-green-800/30 border border-green-300 dark:border-green-700 rounded-lg hover:bg-green-200 dark:hover:bg-green-800/50 transition-colors"
                >
                  Manage Trial
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Business Metrics (Admin Only) */}
        <MetricsCards />

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          <Link
            to="/onboarding"
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border dark:border-gray-700 p-4 sm:p-6 hover:shadow-xl transition-all duration-200 group"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
                  Create New Agent
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 truncate">
                  {agentCount.canCreateMore ? `${agentCount.remaining} of ${agentCount.max} agents available` : `${agentCount.current} of ${agentCount.max} agents created`}
                </p>
              </div>
            </div>
          </Link>

          <Link
            to="/bookings"
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border dark:border-gray-700 p-4 sm:p-6 hover:shadow-xl transition-all duration-200 group"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
                  View Bookings
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 truncate">
                  Manage all appointments
                </p>
              </div>
            </div>
          </Link>

          <Link
            to="/analytics"
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border dark:border-gray-700 p-4 sm:p-6 hover:shadow-xl transition-all duration-200 group"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                    View Analytics
                  </h3>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 flex-shrink-0">
                    BETA
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                  Track performance & insights
                </p>
              </div>
            </div>
          </Link>

          <Link
            to="/settings"
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border dark:border-gray-700 p-4 sm:p-6 hover:shadow-xl transition-all duration-200 group"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
                  Settings
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 truncate">
                  Configure your account
                </p>
              </div>
            </div>
          </Link>
        </div>

        {/* My Agents */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              My <span className="bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent">AI Agents</span>
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Manage and monitor your AI assistants
            </p>
          </div>
          
          <div className="p-6">
            {isLoading ? (
              <div className="text-center py-12">
                <LoadingSpinner size="lg" text="Loading your agents..." />
              </div>
            ) : agents.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No agents yet
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Create your first AI agent to get started with automated customer service.
                </p>
                {agentCount.canCreateMore ? (
                  <Link
                    to="/onboarding"
                    className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-lg text-white bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 hover:shadow-lg hover:scale-105 transition-all duration-200"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Create Your First Agent
                  </Link>
                ) : !user?.hasActiveSubscription && !user?.isInGracePeriod ? (
                  <button
                    disabled={true}
                    className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-lg text-white bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-60"
                  >
                    <span className="flex items-center">
                      Coming Soon
                      <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </span>
                  </button>
                ) : (
                  <div className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-lg text-gray-400 bg-gray-200 dark:bg-gray-700 cursor-not-allowed">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                    </svg>
                    Agent Limit Reached ({agentCount.current}/{agentCount.max})
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {agents.map((agent) => (
                  <div key={agent.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mr-4">
                          <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {agent.businessName}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            {agent.businessType.charAt(0).toUpperCase() + agent.businessType.slice(1)} • Created {new Date(agent.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          onClick={() => handleCopyChatLink(agent.id)}
                          className="inline-flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-all duration-200"
                        >
                          <svg className="w-4 h-4 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          <span className="hidden sm:inline">Copy Link</span>
                        </button>
                        <button
                          onClick={() => handleShowQRCode(agent.id, agent.businessName)}
                          className="inline-flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-all duration-200"
                        >
                          <svg className="w-4 h-4 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                          </svg>
                          <span className="hidden sm:inline">QR Code</span>
                        </button>
                        <a
                          href={`/chat/${agent.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                        >
                          <svg className="w-4 h-4 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          <span className="hidden sm:inline">View Chat</span>
                        </a>
                        <button
                          onClick={() => handleOpenAgentSettings(agent.id)}
                          className="inline-flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                        >
                          <svg className="w-4 h-4 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="hidden sm:inline">Settings</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className="text-center mt-6">
                  {agentCount.canCreateMore ? (
                    <Link
                      to="/onboarding"
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Create Another Agent ({agentCount.remaining} left)
                    </Link>
                  ) : !user?.hasActiveSubscription && !user?.isInGracePeriod ? (
                    <button
                      disabled={true}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium bg-gray-400 dark:bg-gray-600 text-white rounded-lg cursor-not-allowed opacity-60"
                    >
                      <span className="flex items-center">
                        Coming Soon
                        <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </span>
                    </button>
                  ) : (
                    <div className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-400 cursor-not-allowed">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                      </svg>
                      Agent Limit Reached ({agentCount.current}/{agentCount.max})
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Account Information */}
        <div className="mt-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg border dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Account Information
            </h2>
          </div>
          
          <div className="p-6">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Name
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                  {user?.firstName} {user?.lastName}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Email Address
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                  {user?.email}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Plan
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                  {user?.subscriptionStatus === 'trialing' 
                    ? `Svarra Complete (Trial - ${user.trialDaysRemaining || 0} days left)`
                    : user?.hasActiveSubscription 
                    ? 'Svarra Complete' 
                    : 'Free Plan'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Member Since
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                  {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Agent Settings Modal */}
      <AgentSettings
        agentId={selectedAgentId}
        isOpen={isSettingsOpen}
        onClose={handleCloseAgentSettings}
        onSave={handleAgentUpdated}
      />

      {/* QR Code Modal */}
      {qrCodeAgent && (
        <QRCodeModal
          isOpen={!!qrCodeAgent}
          onClose={handleCloseQRCode}
          chatUrl={`${window.location.protocol}//${window.location.host}/chat/${qrCodeAgent.id}`}
          businessName={qrCodeAgent.businessName}
        />
      )}
      
      {/* Svarra Business Advice Bot */}
      {(() => {
        const agentId = agents && agents.length > 0 ? agents[0].id : undefined;
        console.log('🔍 Dashboard passing agentId to SvarraAdviceBot:', agentId);
        console.log('🔍 Available agents:', agents);
        return <SvarraAdviceBot selectedAgentId={agentId} />;
      })()}

      {/* Feedback Widget */}
      <FeedbackWidget />
    </div>
  );
};

export default Dashboard;